import { styled } from "goober";
import { type ReactNode, useRef } from "react";
import { useScrollFade } from "../../hooks/useScrollFade";
import {
  MONITORING_CARD_MAX,
  MONITORING_PANEL_PADDING,
  MONITORING_SHORT_VIEWPORT_HEIGHT,
} from "./monitoringPanelLayout";

// Shared vertical bar-chart primitives for the monitoring dashboard, used by
// "Executions by space" and "Spend by model" so the two charts read identically.

const ScrollColumns = styled("div")`
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
  min-width: 0;
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;

  /* Hovering a column widens its bar and fades the rest — CSS-only, so there's
     no hovered-id state re-rendering the chart on every mouse move. */
  [data-bar-column]:hover [data-bar] {
    width: 9px;
  }
  &:has([data-bar-column]:hover) [data-bar-column]:not(:hover) {
    opacity: 0.4;
  }

  /* Soft faded edges once the columns overflow, driven by useScrollFade. */
  -webkit-mask-image: linear-gradient(
    to right,
    transparent,
    black var(--fade-left, 0px),
    black calc(100% - var(--fade-right, 0px)),
    transparent
  );
  mask-image: linear-gradient(
    to right,
    transparent,
    black var(--fade-left, 0px),
    black calc(100% - var(--fade-right, 0px)),
    transparent
  );

  /* Single-row (short) layout: hug the bars, capped at the card's content box
     so the fade renders inside the padding, not under the card edge. Auto
     margins center the track in a fixed-width card (Spend by model) while
     keeping justify-content: flex-start, so overflow stays scrollable. */
  @media (min-width: 901px) and (max-height: ${MONITORING_SHORT_VIEWPORT_HEIGHT}px) {
    width: fit-content;
    max-width: calc(
      ${MONITORING_CARD_MAX}px - 2 * ${MONITORING_PANEL_PADDING}px
    );
    margin-inline: auto;
  }
`;

// useScrollFade must mount alongside the container: panels early-return a
// skeleton, so a hook on the parent would run with a null ref and never re-fire.
export function FadeScrollColumns({
  children,
  ariaHidden,
}: {
  children: ReactNode;
  ariaHidden?: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  useScrollFade({ scrollRef });
  return (
    <ScrollColumns ref={scrollRef} aria-hidden={ariaHidden || undefined}>
      {children}
    </ScrollColumns>
  );
}

// Shared as a string so an interactive (PressButton) column can look identical.
// The min width keeps labels legible: the row scrolls rather than compressing.
export const barColumnStyles = `
  flex: 1 0 64px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  border-radius: 8px;
  transition: opacity var(--motion-fast) var(--ease-out);

  @media (min-width: 901px) and (max-height: ${MONITORING_SHORT_VIEWPORT_HEIGHT}px) {
    flex: 0 0 64px;
    max-width: 64px;
  }
`;

export const BarColumn = styled("div")`
  ${barColumnStyles}
`;

export const BarRow = styled("div")`
  width: 100%;
  flex: 1;
  min-height: 100px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

export const Bar = styled("div")`
  width: 6px;
  border-radius: 999px;
  transition:
    width var(--motion-fast) var(--ease-out),
    opacity var(--motion-fast) var(--ease-out),
    filter var(--motion-fast) var(--ease-out);
`;

export const ColumnMeta = styled("div")`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  text-align: center;
`;

// A plain (non-scrolling) columns row, so panels that manage their own scroll
// container (the connectors request-volume bars) can build on the same hover
// behaviour without inheriting FadeScrollColumns' fixed markup.
export const Columns = styled("div")`
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  flex: 1;

  [data-bar-column]:hover [data-bar] {
    width: 9px;
  }
  &:has([data-bar-column]:hover) [data-bar-column]:not(:hover) {
    opacity: 0.4;
  }
`;

// Bars are scaled logarithmically so values spanning several orders of
// magnitude stay visible and comparable.
export function logBarFraction(value: number, max: number): number {
  if (value <= 0 || max <= 0) return 0;
  if (max === 1) return 1;
  return Math.log1p(value) / Math.log1p(max);
}
